'use client';

import { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { getSignInUrl, getSignUpPageUrl } from '@/lib/app-urls';

const NAV_LINKS = [
  { href: '/',         label: 'Home' },
  { href: '/features', label: 'Features' },
  { href: '/gallery',  label: 'Gallery' },
  { href: '/pricing',  label: 'Pricing' },
];

const Navbar = () => {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const signInUrl = getSignInUrl();
  const signUpUrl = getSignUpPageUrl();

  const getLinkClass = (href) => {
    const isActive = pathname === href;
    const base = 'text-muted hover:text-ink transition-colors duration-200';
    const active = 'text-ink font-medium border-b border-ink pb-[2px]';
    return isActive ? `${base} ${active}` : base;
  };

  const getMobileLinkClass = (href) => {
    const isActive = pathname === href;
    const base = 'text-[22px] transition-colors duration-200 block py-2';
    const active = 'text-ink font-medium';
    const inactive = 'text-muted hover:text-ink';
    return isActive ? `${base} ${active}` : `${base} ${inactive}`;
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-[rgba(251,250,246,0.88)] backdrop-blur-[12px] border-b hairline">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8 lg:px-10 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-3 no-underline" onClick={() => setIsMenuOpen(false)}>
          <span className="w-8 h-8 rounded-full bg-ink border border-gold flex items-center justify-center serif text-[16px] text-gold-soft shrink-0">∞</span>
          <span className="serif text-[24px] tracking-[-.02em] text-ink">
            Time<span className="text-gold">Lens</span>
          </span>
        </Link>

        {/* Nav links */}
        <nav className="hidden md:flex items-center gap-9 text-[14px]" aria-label="Main navigation">
          {NAV_LINKS.map(({ href, label }) => (
            <Link key={href} href={href} className={getLinkClass(href)}>
              {label}
            </Link>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <a href={signInUrl} className="hidden sm:inline-block text-[14px] text-muted hover:text-ink no-underline transition-colors duration-200">Log in</a>
          <a href={signUpUrl} className="inline-block text-[13px] font-medium tracking-[.02em] px-5 py-[9px] rounded-full bg-ink text-white no-underline transition-colors duration-200 hover:bg-gold">Sign up</a>

          {/* Mobile Menu Toggle */}
          <button 
            className="md:hidden flex flex-col justify-center items-center w-8 h-8 space-y-[5px] focus:outline-none" 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-[2px] bg-ink transition-transform duration-300 ease-in-out ${isMenuOpen ? 'rotate-45 translate-y-[7px]' : ''}`}></span>
            <span className={`block w-6 h-[2px] bg-ink transition-opacity duration-300 ease-in-out ${isMenuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`block w-6 h-[2px] bg-ink transition-transform duration-300 ease-in-out ${isMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''}`}></span>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`md:hidden absolute top-20 left-0 w-full bg-[rgba(251,250,246,0.95)] backdrop-blur-md border-b hairline shadow-2xl transition-all duration-400 ease-in-out overflow-hidden ${isMenuOpen ? 'max-h-[500px] py-8 opacity-100 visible' : 'max-h-0 py-0 opacity-0 invisible'}`}
      >
        <div className="px-6 flex flex-col space-y-4 text-center">
          {NAV_LINKS.map(({ href, label }) => (
            <Link 
              key={href} 
              href={href} 
              className={getMobileLinkClass(href)}
              onClick={() => setIsMenuOpen(false)}
            >
              {label}
            </Link>
          ))}
          <div className="pt-6 mt-2 border-t hairline flex flex-col items-center gap-4 sm:hidden">
            <a href={signInUrl} className="text-[16px] text-muted hover:text-ink no-underline transition-colors duration-200 font-medium" onClick={() => setIsMenuOpen(false)}>Log in</a>
            <a href={signUpUrl} className="inline-block text-[14px] font-medium tracking-[.02em] px-6 py-[11px] rounded-full bg-ink text-white no-underline transition-colors duration-200 hover:bg-gold" onClick={() => setIsMenuOpen(false)}>Sign up</a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
